// The intensity bands and the grid's shape. The grid itself is built from a
// Draft (programIntensityDraft.ts): the one built from a stored Program went
// with the old preview (#358). No React, no i18n, no catalog.
//
// The grid is the program's SHAPE at a glance: per (session, week) we map
// the HARDEST prescribed set to an intensity band. Intensity comes from
// EITHER %-of-MAX (load) OR prescribed effort (RPE/RIR) — both are
// legitimate "how hard is this day" signals, so a day programmed entirely
// at RPE (a hypertrophy/specialization day) still colours instead of
// reading as an empty black row. `max` (the peak band, user-facing "Max")
// stays reserved for %-of-MAX >= 85 — an RPE-10 isolation set is not a
// 1RM attempt.

export type IntensityBand = 'work' | 'moderate' | 'heavy' | 'max';

// Exported for sibling derivations (draft grid, plan matrix) that need
// "does this band beat that one" without re-stating the order.
export const BAND_RANK: Record<IntensityBand, number> = { work: 1, moderate: 2, heavy: 3, max: 4 };
const RANK = BAND_RANK;

// The band order for legends / iteration — one home instead of the verbatim
// `const BANDS = [...]` that IntensityGridView and PlanIntensityMatrix each
// used to re-declare.
export const BANDS: IntensityBand[] = ['work', 'moderate', 'heavy', 'max'];

// Band → glyph. The heat cells carry the band by fill COLOUR; this glyph is
// the non-colour cue so the band survives colour-blindness and a mono render
// (the never-colour-only floor) — the intensity-scale twin of TargetBar's
// ZONE_GLYPH. `max` → "X" because "M" already names `moderate`. Both heat
// renderers (day×week grid, plan exercise×set matrix) read it, so it lives
// here beside IntensityBand rather than in either component.
export const BAND_GLYPH: Record<IntensityBand, string> = {
  work: 'W',
  moderate: 'M',
  heavy: 'H',
  max: 'X',
};

// The localized band word for legends / a11y labels. Pure (takes the caller's
// `t`, like TargetBar's zoneWord) so both heat renderers print the same label
// without re-declaring the i18n key — the helper the two `const bandLabel`
// copies collapsed into.
export const bandLabel = (
  t: (k: string, opts?: { defaultValue: string }) => string,
  band: IntensityBand,
): string => t(`mobile.preview.intensity.${band}`, { defaultValue: band });

// Absolute %-of-MAX thresholds. `>=` convention on ALL three (a value
// exactly on a threshold belongs to the higher band): >=85 max, >=76
// heavy, >=68 moderate, else work. Mixing `>` and `>=` leaves boundary
// holes — a max attempt prescribed at exactly 85 would demote to heavy.
// Absolute (not normalised per-program) so 75% reads as moderate
// physiologically instead of inflating to "heavy" in a light block.
export const INTENSITY_BANDS = { max: 85, heavy: 76, moderate: 68 } as const;

export function bandOf(pct: number | null): IntensityBand | null {
  if (pct == null) return null;
  if (pct >= INTENSITY_BANDS.max) return 'max';
  if (pct >= INTENSITY_BANDS.heavy) return 'heavy';
  if (pct >= INTENSITY_BANDS.moderate) return 'moderate';
  return 'work';
}

// Effort → band. RPE is high-good (10 = max), RIR is low-good (0 = to
// failure). Neither ever reaches `max` — that band marks an actual
// %-of-MAX peak/attempt set, not a hard accessory.
export const rpeBand = (t: number): IntensityBand => (t >= 9 ? 'heavy' : t >= 7.5 ? 'moderate' : 'work');
export const rirBand = (t: number): IntensityBand => (t <= 1 ? 'heavy' : t <= 3 ? 'moderate' : 'work');

// The harder of two bands (null = no signal, loses to any band). Exported
// for the plan-preview matrix (planIntensity.ts), which folds a planned
// exercise's per-set bands into one row band the same way.
export const higherBand = (
  a: IntensityBand | null,
  b: IntensityBand | null,
): IntensityBand | null => {
  if (!a) return b;
  if (!b) return a;
  return RANK[b] > RANK[a] ? b : a;
};

export type WeekCell = {
  band: IntensityBand | null;
  // Short label for the band-driving set, always rendered on the cell:
  // %-of-MAX → "78", RPE → "9", RIR → "2". null = no banded signal.
  value: string | null;
  // Prescribed sets that week across the day's slots. Distinguishes "no
  // banded intensity but training happens" (a free/none day renders an
  // outlined cell with the set count, e.g. "18×") from a truly empty
  // week (inactive slot / no sets), which stays flat neutral.
  sets: number;
};

export type DayIntensity = {
  sessionIdx: number;
  name: string;
  cells: WeekCell[]; // length === program.cycleWeeks
  band: IntensityBand | null; // hardest band across the cycle (the day badge)
};

export type IntensityGrid = {
  weeks: number;
  days: DayIntensity[];
};
